/**
 * The panel beside the canvas: the selected step's or link's inspector, or a
 * hint when nothing is selected. Removing a step or a link happens in its
 * inspector, never by a key press on the canvas, so the operator sees what
 * goes with it. A gate node and a condition node (Feature 79) have their own.
 */
import { Typography } from '../../ui/finixt.js';
import type { SelectOption } from '../../ui/Select.js';
import { findCondition } from './conditions.js';
import { ConditionInspector } from './ConditionInspector.js';
import { FIRST, type Draft } from './draft.js';
import { FirstInspector } from './FirstInspector.js';
import { GateInspector } from './GateInspector.js';
import { InspectorPanel } from './InspectorPanel.js';
import { LinkInspector } from './LinkInspector.js';
import type { Schemas } from './references.js';
import type { Selection } from './selection.js';
import { StepInspector } from './StepInspector.js';

export function LinkageInspector({
  draft,
  isNew,
  selection,
  capabilities,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  /** Whether the template is not yet saved: its start may change and its first node go. */
  isNew: boolean;
  selection: Selection;
  capabilities: readonly SelectOption[];
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  if (selection?.kind === 'edge') {
    return (
      <LinkInspector
        draft={draft}
        from={selection.from}
        to={selection.to}
        schemas={schemas}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  if (selection?.kind === 'node' && findCondition(draft.decisions, selection.id)) {
    return (
      <ConditionInspector
        draft={draft}
        id={selection.id}
        schemas={schemas}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  if (selection?.kind === 'node' && draft.decisions.some((d) => d.name === selection.id)) {
    return (
      <GateInspector
        draft={draft}
        name={selection.id}
        schemas={schemas}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  if (selection?.kind === 'node' && selection.id === FIRST) {
    return (
      <FirstInspector
        draft={draft}
        isNew={isNew}
        capabilities={capabilities}
        schemas={schemas}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  if (selection?.kind === 'node') {
    return (
      <StepInspector
        draft={draft}
        name={selection.id}
        capabilities={capabilities}
        schemas={schemas}
        onChange={onChange}
        onSelect={onSelect}
      />
    );
  }
  return (
    <InspectorPanel>
      <Typography variant="eyebrow">Nothing selected</Typography>
      <Typography variant="meta">
        Select a step, a gate, or a link to edit it. Drag from a step's right side to another step's
        left side to link them.
      </Typography>
    </InspectorPanel>
  );
}
